/** @jsxImportSource @emotion/react */

import { useOutsideClick } from "@imwebme/clay-hooks";
import type { ElementType, ReactElement } from "react";
import type { ClayProps } from "../utilities";
import { Flex } from "../utilities";
import { useTooltipContext } from "./Tooltip.provider";

export type TooltipTriggerProps<T extends ElementType> = ClayProps<T> & {
  /**
   * 툴팁을 호출하는 action을 설정할 수 있습니다.
   * @default 'click'
   */
  action?: "hover" | "click";
  /**
   * 툴팁의 노출 여부를 제어할 수 있습니다.
   * @default true
   */
  enabled?: boolean;
};

export type TooltipTriggerComponent = <C extends ElementType = "div">(
  props: TooltipTriggerProps<C>,
) => ReactElement | null;

/**
 * 툴팁을 호출하는 트리거 wrapper입니다.
 *
 * @param TooltipTriggerProps
 * @example
 * <Tooltip>
 *  <Tooltip.trigger>
 *    <button>hi</button>
 * </Tooltip.trigger>
 * </Tooltip>
 */
export const TooltipTrigger: TooltipTriggerComponent = <E extends ElementType>({
  as,
  children,
  enabled = true,
  action = "hover",
  native,
  ...restProps
}: TooltipTriggerProps<E>) => {
  const AS = as || "button";
  const { setOff, isOpen, toggle, setPortalRef, portalRef } = useTooltipContext();

  useOutsideClick({
    ref: portalRef,
    callback: () => {
      if (isOpen) {
        setOff();
      }
    },
    enabled,
  });

  return (
    <Flex
      as={AS}
      ref={setPortalRef}
      native={{
        onClick: action === "click" && enabled ? toggle : undefined,
        onMouseEnter: action === "hover" && enabled ? toggle : undefined,
        onMouseLeave:
          action === "hover" && enabled
            ? () => {
                setOff();
              }
            : undefined,
        ...(AS === "button" && { type: "button" }),
        ...native,
      }}
      position="relative"
      {...restProps}
    >
      {children}
    </Flex>
  );
};
